// [PURPOSE]: Roll-table entry migrator — converts legacy
//   `{min, max, item|template|name, rarity}` shapes into the
//   `{min, max, kind, ref, qty, ...}` schema agreed in the
//   2026-05-06 design dialog (Bucket 2 / B4-1).
//
// [WHY]: Phase B / #033. Pre-migration, custom roll tables stored
//   entries as opaque strings — the loot-grant pipeline (#034)
//   couldn't tell whether a rolled entry was an item, a currency
//   amount, a condition cause, or pure flavor text. The `kind`
//   discriminator unblocks structured grants without breaking the
//   existing roll consumers (shopGenerator, customInvRef resolver,
//   templateResolvers).
//
// [INTEGRATION]:
//   - shopGenerator.rollFromTable() calls migrateEntry on the chosen
//     entry before returning, so callers always see the structured
//     shape.
//   - CustomRollTableDialog calls migrateAllEntries on the table
//     it loads, so the editor surface is also structured. Fields
//     authored in the dialog get the `kind` discriminator immediately.
//   - DMToolsPanel / loot-push pipeline (#034) reads `entry.kind` to
//     dispatch grants.
//
// [LOSSLESS]: Legacy fields (`item`, `template`, `n`, `name`,
//   `rarity`) are preserved on migrated entries so the existing
//   roll-time resolvers keep working. New consumers read `kind` +
//   `ref`; old consumers continue to read `item` / `template` / etc.
//
// [IDEMPOTENT]: Already-structured entries (with `kind` set) pass
//   through by reference. Roll-time dispatch can call the migrator
//   freely without allocating per-roll.

/**
 * Closed enum of supported entry kinds. Confirmed in the 2026-05-06
 * design dialog (Q1). Adding a new kind requires a code change here
 * AND a dispatcher update in #034's grantRollResult helper.
 */
export const ENTRY_KINDS = Object.freeze({
  ITEM: 'item',
  CURRENCY: 'currency',
  CONDITION: 'condition',
  STRING_ROLL: 'stringRoll',
  FEAT: 'feat',
  SPELL: 'spell',
});

const KIND_VALUES = new Set(Object.values(ENTRY_KINDS));

/**
 * Migrate a single roll-table entry to the `{kind, ref, qty}` schema.
 *
 * @param {object|null|undefined} entry
 * @returns {object|null} the structured entry (same reference if
 *   already structured), or `null` for malformed input.
 */
export function migrateEntry(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return null;
  }
  // Range fields are required — without them the table can't roll
  // against the entry.
  if (typeof entry.min !== 'number' || typeof entry.max !== 'number') {
    return null;
  }

  // Already structured — pass through by reference (idempotent).
  if (entry.kind && KIND_VALUES.has(entry.kind)) {
    return entry;
  }

  // Template-driven entries (DMG d100 + custom). Resolve at roll
  // time via TEMPLATE_RESOLVERS — `ref` stays null on the migrated
  // shape because the resolved item id isn't known until the roll.
  if (typeof entry.template === 'string' && entry.template.length > 0) {
    return {
      ...entry,
      kind: ENTRY_KINDS.ITEM,
      ref: null,
      qty: typeof entry.qty === 'number' ? entry.qty : 1,
    };
  }

  // Flat-ID entries (DMG d100 + custom). `item` is the dispatch tag —
  // could be a BASE_ITEMS key, a `custom_inv_<idx>` ref, or a
  // `custom_inv_<idx>_contents_<sub>` nested ref. The `customInvRef`
  // resolver dispatches at roll time.
  if (typeof entry.item === 'string' && entry.item.length > 0) {
    return {
      ...entry,
      kind: ENTRY_KINDS.ITEM,
      ref: entry.item,
      qty: typeof entry.qty === 'number' ? entry.qty : 1,
    };
  }

  // Pure flavor / narrative — `name` carries the activity-log line.
  // No item granted; the loot-grant pipeline produces a log entry
  // and moves on.
  if (typeof entry.name === 'string' && entry.name.length > 0) {
    return {
      ...entry,
      kind: ENTRY_KINDS.STRING_ROLL,
      ref: entry.name,
    };
  }

  return null;
}

/**
 * Migrate an array of entries in one call. Drops malformed entries
 * (the returned array may be shorter than the input).
 *
 * @param {Array|any} entries
 * @returns {Array} migrated entries (always an array, possibly empty).
 */
export function migrateAllEntries(entries) {
  if (!Array.isArray(entries)) return [];
  const out = [];
  for (const e of entries) {
    const migrated = migrateEntry(e);
    if (migrated !== null) out.push(migrated);
  }
  return out;
}
